import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, inject, input, OnInit, output, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconComponent } from '@library/ui/atoms/icon/icon.component';
import { IconModule } from '@library/ui/atoms/icon/icon.module';
import { TextModule } from '@library/ui/atoms/text/text.module';
import { LangPipe } from '@library/utils/pipes/lang.pipe';
import { JawgMapsService } from '@library/utils/services/jawgmaps.service';
import type { LngLat, Map, Marker } from 'maplibre-gl';
import { fromEvent } from 'rxjs';

@Component({
  selector: 'app-location-map',
  templateUrl: './location-map.component.html',
  styleUrl: './location-map.component.scss',
  imports: [
    IconModule,
    TextModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationMapComponent implements OnInit, AfterViewInit {
  private readonly geolocateButton = viewChild<IconComponent, ElementRef<HTMLElement>>('geolocateButton', { read: ElementRef });

  private readonly destroyRef = inject(DestroyRef);
  private map: Map;
  private marker: Marker;

  coordinates = input.required<LngLat>();
  altitude = input<string>('0');
  showAltitude = input<boolean>(false);
  showGeolocateButton = input<boolean>(true);
  zoom = input<number>(18);
  draggableMarker = input<boolean>(true);

  changePosition = output<LngLat>();

  constructor(
    private readonly langPipe: LangPipe,
    private readonly jawgMapsService: JawgMapsService,
  ) {
    // update position when coordinates change
    effect(() => {
      this.moveToCoordinates(this.coordinates());
    });
  }

  async ngOnInit(): Promise<void> {
    await this.jawgMapsService.loadMapLibre();

    // create map
    this.map = this.jawgMapsService.createMap('map', {
      center: this.coordinates(),
      zoom: this.zoom(),
    });

    // add marker to map
    this.marker = this.jawgMapsService.createMarker(this.coordinates(), {
      draggable: this.draggableMarker(),
    });
    this.marker.addTo(this.map);

    // update new position when marker is dragged
    if (this.marker.isDraggable()) {
      this.marker.on('dragend', () => {
        const coordinates = this.marker.getLngLat();
        this.moveToCoordinates(coordinates);
        this.changePosition.emit(coordinates);
      });
    }
  }

  ngAfterViewInit(): void {
    if (!this.showGeolocateButton()) {
      return;
    }

    const geolocateButton = this.geolocateButton().nativeElement;

    if (!navigator.geolocation) {
      geolocateButton.innerHTML = this.langPipe.transform('common-settings.__NO_GEOLOCATION');
      return;
    }

    geolocateButton.style.cursor = 'pointer';
    fromEvent(geolocateButton, 'click')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        navigator.geolocation.getCurrentPosition((position) => {
          const coordinates = new this.jawgMapsService.maplibre.LngLat(position.coords.longitude, position.coords.latitude);
          this.moveToCoordinates(coordinates);
          this.changePosition.emit(coordinates);
        });
      });
  }

  moveToCoordinates(coordinates: LngLat): void {
    if (!this.map || !this.marker) {
      return;
    }

    this.map.panTo(coordinates);
    this.marker.setLngLat(coordinates);
  }
}
